// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { useEffect, useState } from "react"
import {
  IonDatetime,
  IonGrid,
  IonItem,
  IonItemGroup,
  IonList,
  IonRow,
  IonText
} from "@ionic/react"
import { useLocales } from "react-localized"
import { ResponsiveIonCol } from "./ResponsiveIonCol"
import { Location, ProcurementClient } from "../api"
import { PurchaseOrder } from "../models"
import { BuyerItem } from "./BuyerModal"
import { IncoTermsItem } from "./IncoTermsModal"
import { SelectLocationItem } from "./LocationModal"
import { SelectSupplierItem } from "./SelectSupplierModal"
import { SelectCurrencyItem } from "./SelectCurrencyModal"
import { FullscreenInputItem, ScreenSizeConditional } from "../lib"


interface PlacePurchaseOrderProps {
  cancelButton: React.ReactElement
  nextButton: React.ReactElement
  order: PurchaseOrder
}


const PlacePurchaseOrder: React.FC<PlacePurchaseOrderProps> = ({
  cancelButton,
  nextButton,
  order
}) => {
  const client = new ProcurementClient()
  const orderDto = order.asDataTransferObject()
  const {gettext} = useLocales()
  const incoTermOptions: any = {
    "N_A": gettext("Not applied"),
    "EXW": gettext("Ex works (EXW)"),
    "FCA_A": gettext("Free Carrier (FCA-A)"),
    "FCA_B": gettext("Free Carrier (FCA-B)"),
    "DAP": gettext("Delivered At Place (DAP)"),
    "CIP": gettext("Carriage and Insurance Paid To (CIP)"),
    "CPT": gettext("Carriage Paid To (CPT)"),
  }
  const [buyer, setBuyer] = useState<{value: string, label: string}>({value: "Molano B.V.", label: "Molano B.V."})
  const [currency, setCurrency] = useState<{label: string, value: string}>({label: "EUR", value: "EUR"})
  const [deliveryTerm, setDeliveryTerm] = useState<{value: string, label: string}>({label: "Ex works (EXW)", value: "EXW"})
  const [expectedDelivery, setExpectedDelivery] = useState<string>(orderDto.expected_delivery || "")
  const [location, setLocation] = useState<{value: Location}>(null!)
  const [supplierId, setSupplierId] = useState<{display_name: string, supplier_id: number, email: string}>(null!)

  const loadLocations = async () => {
    return (await client.getLocations()).map((location) => {
      return location
    })
  }

  useEffect(() => {
    const values: any = {}
    if (!!buyer) { values["buyer"] = buyer.value}
    if (!!currency) { values["currency"] = currency.value}
    if (!!deliveryTerm) { values["incoterms"] = deliveryTerm.value}
    if (!!expectedDelivery) {
      values["expected_delivery"] = expectedDelivery.split('T')[0]
    }
    if (!!location) { values["port_of_delivery"] = location.value.asDataTransferObject()}
    if (!!supplierId) { values["supplier"] = {
      email: supplierId.email,
      id: supplierId.supplier_id,
      name: supplierId.display_name,
    }}
    if (!!values) {
      order.persist(values)
    }
  }, [buyer, currency, deliveryTerm, expectedDelivery, location, supplierId])

  return (
    <IonGrid style={{paddingTop: "var(--section-padding)"}}>
      <IonRow className="panel">
        <ResponsiveIonCol>
          <IonList lines="none">
            <IonItemGroup>
              <BuyerItem
                placeholder={order.getBuyerName() || gettext("Click or tap to select the Buyer")}
                value={buyer}
                onSelected={setBuyer}/>
              <SelectSupplierItem
                onSelected={setSupplierId}
                placeholder={order.getSupplierName() || gettext("Click or tap to select the Supplier")}
                value={supplierId}
              />
              <SelectCurrencyItem
                value={currency}
                onSelected={setCurrency}
                placeholder={order.getCurrency() || gettext('Click or tap to select the currency')}
              />
              <SelectLocationItem
                loadOptions={loadLocations}
                onSelected={setLocation}
                placeholder={order.getAddressFormatted() || gettext("Click or tap to select the delivery location")}
                value={location}
              />
              <FullscreenInputItem
                inputComponent={(ctrl) => {
                  return (
                    <>
                      <IonDatetime
                        onIonChange={(event) => {
                          if (typeof event.target.value !== "string") { return }
                          setExpectedDelivery(new Date(event.target.value).toISOString())
                          ctrl.closeModal()
                        }}
                        presentation="date"
                        style={{marginTop: "22px"}}
                        value={expectedDelivery || (new Date()).toISOString()}
                      />
                    </>
                  )
                }}
                placeholder={
                  (!!expectedDelivery)
                  ? new Date(expectedDelivery).toDateString()
                  : gettext("Click or tap to set the expected delivery date.")
                }
                title={gettext("Expected delivery date")}
                value={expectedDelivery}
              >
                <IonText>
                  <p>{gettext("The expected delivery date is used by this app to plan and predict incoming shipments.")}</p>
                </IonText>
              </FullscreenInputItem>
              <IncoTermsItem
                onSelected={setDeliveryTerm}
                placeholder={
                  (!deliveryTerm) ? gettext("Not specified") : incoTermOptions[deliveryTerm.value] 
                }
                value={deliveryTerm}
              />
            </IonItemGroup>
            <ScreenSizeConditional full>
              <IonItemGroup>
                <IonItem>
                  {nextButton}
                </IonItem>
              </IonItemGroup>
            </ScreenSizeConditional>
          </IonList>
        </ResponsiveIonCol>
      </IonRow>
    </IonGrid>
  )
}


export {PlacePurchaseOrder}